import { Injectable } from '@angular/core';

import {
  projectActivitiesAdd,
  projectActivitiesCopy,
  projectActivitiesRemove,
  projectActivitiesReorder,
  projectByIdDelete,
  projectByIdDetail,
  projectByIdUpdateDescription,
  projectByIdUpdateName,
  projectsCreate,
  projectsList,
} from '@api/sdk.gen';
import type {
  CreateProjectRequest,
  ProjectActivityItem,
  ProjectDetail,
  ProjectListResponse,
} from '@api';

/**
 * Thrown by {@link ProjectsApiService} when a projects-API call fails to
 * produce a usable DTO — either a non-2xx response, or a 2xx response with
 * no body. Carries the HTTP `status` (recovered via `throwOnError: false` +
 * `responseStyle: 'fields'`, since hey-api's `throwOnError: true` path
 * throws the parsed error body and loses the status) so callers can
 * distinguish e.g. `400` (validation) from other failures without
 * depending on hey-api internals.
 *
 * `kind` discriminates *why* the call failed:
 * - `'http'`: an HTTP response was received (`response.ok !== true`, i.e.
 *   a non-2xx status); `status` is the real HTTP status and `cause` is the
 *   parsed error body (if any).
 * - `'empty-body'`: a 2xx response was received with no parseable body;
 *   `status` is the 2xx status and `cause` is normally absent. (Edge case:
 *   hey-api can theoretically also land here for a 2xx response whose body
 *   failed to parse — `response.ok === true` but `error !== undefined` —
 *   since that shape isn't a non-ok `'http'` case either; this path is
 *   believed unreachable via `HttpClientTestingModule` and is untested.)
 * - `'transport'`: no usable HTTP response was received at all — either no
 *   `response` reached the client (e.g. an error thrown before the request
 *   was sent), or `status === 0` (e.g. a network failure); `status` is `0`
 *   and `cause` is the underlying transport error.
 */
export class ProjectsApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly kind: 'http' | 'empty-body' | 'transport',
    override readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'ProjectsApiError';
  }
}

/** The subset of a hey-api `responseStyle: 'fields'` result that {@link ProjectsApiService.unwrap} needs. */
interface FieldsResult<TData> {
  data?: TData;
  error?: unknown;
  response?: { readonly status: number; readonly ok: boolean };
}

/**
 * The only layer in the `projects` feature that calls the generated OpenAPI
 * SDK functions (team plan C1 / C4) — `ProjectsStore` and everything above
 * it must go through this adapter instead of calling `@api/sdk.gen`
 * directly. (Reusing the generated DTO *types* from consumers is accepted
 * precedent, e.g. `ProjectListResponse`/`ProjectDetail` — only the SDK
 * *function* calls are confined here.)
 */
@Injectable({ providedIn: 'root' })
export class ProjectsApiService {
  /**
   * The caller's project list, `createdAt` desc with `id` tie-break (server-ordered).
   *
   * @param options - `activityId` narrows the list to the projects that contain that
   * activity (KT-198.2 / D5b); an unknown or foreign activity yields an empty list, never
   * a `404`. Omitted, the full list comes back — the pre-existing behaviour, bit for bit.
   * @returns the matching projects.
   */
  list(options?: { activityId?: string }): Promise<ProjectListResponse> {
    const activityId = options?.activityId;

    return projectsList({
      // The query object is omitted entirely when there is no filter, so the unfiltered
      // call keeps hitting `/v1/projects` with no query string at all.
      ...(activityId === undefined ? {} : { query: { activityId } }),
      throwOnError: false,
      responseStyle: 'fields' as const,
    }).then((result) => ProjectsApiService.unwrap(result, 'Failed to load projects.'));
  }

  /**
   * A single project with its ordered activity memberships (S33: a missing,
   * foreign or non-`Guid` id surfaces as a `404` {@link ProjectsApiError}).
   *
   * @param projectId - the project to load.
   * @returns the project's full `ProjectDetail`.
   */
  detail(projectId: string): Promise<ProjectDetail> {
    return projectByIdDetail({
      path: { projectId },
      throwOnError: false,
      responseStyle: 'fields' as const,
    }).then((result) => ProjectsApiService.unwrap(result, 'Failed to load project.'));
  }

  /**
   * Adds library activities to a project as an atomic batch; resolves with the
   * project's re-read `ProjectDetail` (S12/S87 read-your-writes). A project that
   * no longer exists — including one deleted mid-flight — surfaces as a `404`
   * {@link ProjectsApiError}, which the page turns into the S33 path (K1); an
   * empty or non-UUID id list surfaces as `400`.
   *
   * @param projectId - the project to add to.
   * @param activityIds - the library activity ids to add (at least one).
   * @returns the project's updated `ProjectDetail`.
   */
  addActivities(projectId: string, activityIds: readonly string[]): Promise<ProjectDetail> {
    return projectActivitiesAdd({
      path: { projectId },
      body: { activityIds: [...activityIds] },
      throwOnError: false,
      responseStyle: 'fields' as const,
    }).then((result) =>
      ProjectsApiService.unwrap(result, 'Failed to add activities to the project.'),
    );
  }

  /**
   * Duplicates one activity **into** this project in a single atomic call (KT-338): the
   * server creates the teacher-owned copy and appends its occurrence to this project,
   * so no orphan copy can be left behind and no other project the source belongs to is
   * touched. The source may be a catalog template or the teacher's own instance; the
   * copy is always an `Instance`.
   *
   * `404` = missing/foreign project, or an activity the teacher cannot copy. `409` =
   * the project changed under the write (`Project.Conflict`) — reload and retry.
   *
   * @param projectId - the project the copy is appended to.
   * @param activityId - the activity to copy (not an occurrence id — Q5).
   * @returns the appended occurrence, in the same shape `ProjectDetail` carries.
   */
  copyActivityIntoProject(projectId: string, activityId: string): Promise<ProjectActivityItem> {
    return projectActivitiesCopy({
      path: { projectId, activityId },
      throwOnError: false,
      responseStyle: 'fields' as const,
    }).then((result) =>
      ProjectsApiService.unwrap(result, 'Failed to copy the activity into the project.'),
    );
  }

  /**
   * Breaks one membership row, leaving the library activity itself untouched (S16);
   * resolves with the project's re-read `ProjectDetail`, so the card disappears
   * without a second `GET`.
   *
   * Both failure modes are `404`, and the caller **must** tell them apart by the
   * ProblemDetails `title` (which carries the error code — `ApiResults.Problem`),
   * reachable as this error's `cause`:
   * - `Project.NotFound` — the project is missing or foreign, so it is gone for this
   *   teacher: the S33 redirect to `/projects`.
   * - `Project.ProjectActivityNotFound` — the project **exists**; only this occurrence is
   *   not in it (already removed, or it belongs to another project). Redirecting here would
   *   throw the teacher off a perfectly healthy project, so the page reloads the
   *   detail in place instead.
   *
   * A `409` (`Project.Conflict`) means the project changed under the write: the caller
   * reloads the detail and may retry.
   *
   * A non-`Guid` `projectActivityId` is rejected by the route constraint and also surfaces
   * as `404` (never `400`) — the endpoint's response union is 200/401/404.
   *
   * @param projectId - the project to remove from.
   * @param projectActivityId - the membership row to break (not the activity id — Q5).
   * @returns the project's updated `ProjectDetail`.
   */
  removeActivity(projectId: string, projectActivityId: string): Promise<ProjectDetail> {
    return projectActivitiesRemove({
      path: { projectId, projectActivityId },
      throwOnError: false,
      responseStyle: 'fields' as const,
    }).then((result) =>
      ProjectsApiService.unwrap(result, 'Failed to remove the activity from the project.'),
    );
  }

  /**
   * Átrendezés (Q33/D5): submits the project's **whole desired occurrence order**, not a
   * single move — the server replaces `sortOrder` for the submitted set and answers with
   * the re-read detail. The caller supplies occurrence ids (`projectActivityId`), never
   * activity ids, for the same reason as {@link removeActivity}.
   *
   * `400` = structurally invalid request (empty array, non-UUID element). `404` =
   * missing/foreign project. `409` = the submitted set is not the project's current set
   * (`Project.ActivitySetMismatch` — a stale client list, e.g. an occurrence removed on
   * another device) or the project changed under the write (`Project.Conflict`); both mean
   * "reload and retry", never "the project is gone".
   *
   * @param projectId - the project whose activity order changes.
   * @param projectActivityIds - every occurrence of the project, in the desired order.
   * @returns the project's re-read `ProjectDetail`.
   */
  reorderActivities(projectId: string, projectActivityIds: readonly string[]): Promise<ProjectDetail> {
    return projectActivitiesReorder({
      path: { projectId },
      body: { projectActivityIds: [...projectActivityIds] },
      throwOnError: false,
      responseStyle: 'fields' as const,
    }).then((result) => ProjectsApiService.unwrap(result, 'Failed to reorder activities in the project.'));
  }

  updateName(projectId: string, value: string): Promise<ProjectDetail> {
    return projectByIdUpdateName({
      path: { projectId },
      body: { value },
      throwOnError: false,
      responseStyle: 'fields' as const,
    }).then((result) => ProjectsApiService.unwrap(result, 'Failed to rename the project.'));
  }

  updateDescription(projectId: string, value: string | null): Promise<ProjectDetail> {
    return projectByIdUpdateDescription({
      path: { projectId },
      body: { value },
      throwOnError: false,
      responseStyle: 'fields' as const,
    }).then((result) => ProjectsApiService.unwrap(result, 'Failed to update the project description.'));
  }

  /** Creates a project; resolves with the fresh `ProjectDetail` (201 body). */
  create(request: CreateProjectRequest): Promise<ProjectDetail> {
    return projectsCreate({
      body: request,
      throwOnError: false,
      responseStyle: 'fields' as const,
    }).then((result) => ProjectsApiService.unwrap(result, 'Failed to create project.'));
  }

  /**
   * Deletes a project. The endpoint answers `204 No Content`, so the result is
   * body-less by contract — `assertOk` (not `unwrap`) is what this needs; the
   * latter would reject every successful delete as `'empty-body'`. A foreign or
   * unknown id surfaces as a `404` {@link ProjectsApiError} (S66).
   *
   * @param projectId - the project to delete.
   */
  delete(projectId: string): Promise<void> {
    return projectByIdDelete({
      path: { projectId },
      throwOnError: false,
      responseStyle: 'fields' as const,
    }).then((result) => {
      ProjectsApiService.assertOk(result, 'Failed to delete project.');
    });
  }

  /**
   * Derives the failure half of the {@link ProjectsApiError} taxonomy from a
   * `responseStyle: 'fields'` result: throws on a transport failure or a non-2xx
   * response, otherwise returns the 2xx status. Branches on `response?.ok` /
   * response presence rather than `status === 0` alone, so the check holds even
   * when an error occurs before any HTTP response reaches the client (see
   * {@link ProjectsApiError}'s JSDoc for the full `kind` taxonomy).
   *
   * @returns the successful response's HTTP status.
   */
  private static assertOk(result: FieldsResult<unknown>, message: string): number {
    const { response } = result;

    if (response === undefined || response.status === 0) {
      throw new ProjectsApiError(message, 0, 'transport', result.error);
    }

    // The response alone decides — never `&& result.error !== undefined`. A non-2xx
    // whose error body is absent (e.g. an error interceptor dropped it) is still a
    // failure, and `delete()` has no `unwrap` behind it to catch it as `'empty-body'`:
    // conjoining the two used to make such a response resolve as a *successful* delete.
    if (response.ok !== true) {
      throw new ProjectsApiError(message, response.status, 'http', result.error);
    }

    return response.status;
  }

  /** Like {@link assertOk}, but for the body-carrying calls: also rejects a body-less 2xx. */
  private static unwrap<TData>(result: FieldsResult<TData>, message: string): TData {
    const status = ProjectsApiService.assertOk(result, message);

    // A body-less 2xx (e.g. an empty response) resolves `data` as `null`/`undefined`
    // with no `error` — reject explicitly instead of returning a falsely-typed DTO.
    if (result.data == null) {
      throw new ProjectsApiError(message, status, 'empty-body', result.error);
    }

    return result.data;
  }
}
